import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, X, User, Info, LogOut, Settings } from "lucide-react";
import { useUser } from "../context/UserContext";
import { getMenu } from "../utils/menu";
import { Tooltip } from "@material-tailwind/react";
import { FEATURE_GATE_TOOLTIP } from "../constants/subscriptionFeatures";

const Sidebar = ({ isOpen, toggleSidebar }) => {
    // No menu items have submenus anymore — Reports and Sales Agent both
    // ditched theirs. These helpers stay as no-ops so the existing render
    // code path (which still checks for submenu state) keeps working without
    // a wider refactor.
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { userInfo } = useUser();
    const sidebarRef = useRef(null);
    const [menuItems, setMenuItems] = useState([]);

    // Initialize menu items when user info is available
    useEffect(() => {
        if (userInfo) {
            // Pass the subscription feature JSON so plan-gated items render
            // disabled (rather than being filtered out) for tenants on Plus.
            const subscriptionFeatures =
                userInfo?.loginIdentifierDetails?.subscription?.subscriptionType?.feature;
            setMenuItems(getMenu(userInfo.persona, userInfo.permissions, subscriptionFeatures));
        } else {
            setMenuItems([]);
        }
    }, [userInfo]);

    // Click-outside-to-close: only fires when sidebar is open. We don't auto-
    // open on hover — toggle is explicit via the button at the top of the nav.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                toggleSidebar();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, toggleSidebar]);

    // Link-aware active: normalize path (strip hash), use startsWith, apply path aliases for child/nested routes
    const isLinkActive = (link) => {
        const path = link.path || "";
        const pathBase = path.split("#")[0];
        const p = location.pathname;
        if (p.startsWith(pathBase)) return true;
        if (link.displayName === "Projects" && (p.startsWith("/project-details") || p.startsWith("/create-project"))) return true;
        if (link.displayName === "Buyers" && (p.startsWith("/bv-user/buyers") || p.startsWith("/bv-user/buyer"))) return true;
        if (link.displayName === "Sales" && (p.startsWith("/customers") || p.startsWith("/user-details"))) return true;
        return false;
    };

    const handleMobileSidebarToggle = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const getSubmenuState = () => false;
    const getToggleFunction = () => () => {};

    const renderMenuItem = (link, index, isMobile = false) => {
        const active = isLinkActive(link);
        const isSubmenuOpen = getSubmenuState(link.displayName);
        const toggleSubmenu = getToggleFunction(link.displayName);

        const baseClasses = `flex items-center p-3 m-1 rounded-lg transition-colors duration-200 ${
            active 
                ? "bg-flamingo-400 text-white font-medium" 
                : "hover:bg-flamingo-400 text-gray-700"
        }`;

        const iconClasses = active ? "text-white" : "text-gray-700";
        
        // Larger icons when sidebar is collapsed
        const iconSize = 18;

        if (link.hasSubmenu) {
            return (
                <div key={index} className="relative">
                    <div
                        onClick={() => {
                            toggleSubmenu();
                            if (isMobile && !active) navigate(link.path);
                        }}
                        className={`${baseClasses} cursor-pointer`}
                    >
                        {React.cloneElement(link.icon, { className: iconClasses, size: iconSize })}
                        <span className={`${isOpen ? "block opacity-100" : "hidden opacity-0"} ml-3 text-sm transition-all duration-300 ease-in-out ${active ? "text-white" : "text-black"}`}>
                            {link.displayName}
                        </span>
                        {isOpen && (
                            <ChevronDown
                                size={16}
                                className={`ml-auto transition-all duration-300 ease-in-out ${isSubmenuOpen ? "rotate-180" : "rotate-0"} ${iconClasses}`}
                            />
                        )}
                    </div>
                    
                    {isSubmenuOpen && isOpen && (
                        <div className="ml-4 space-y-1 transition-all duration-300 ease-in-out">
                            {link.submenu.map((subLink, subIndex) => {
                                const subActive = location.pathname === subLink.path;
                                return (
                                    <Link
                                        key={subIndex}
                                        to={subLink.path}
                                        className={`flex items-center p-2 m-2 text-xs rounded-lg transition-colors duration-200 ${
                                            subActive
                                                ? "bg-flamingo-400 text-white font-medium"
                                                : "hover:bg-flamingo-400 text-white"
                                        }`}
                                    >
                                        {React.cloneElement(subLink.icon, {
                                            className: subActive ? "text-white" : "text-gray-700 hover:text-white",
                                            size: 16
                                        })}
                                        <span className={`${isOpen ? "block opacity-100" : "hidden opacity-0"} ml-2 text-xs transition-all duration-300 ease-in-out ${subActive ? "text-white" : "text-gray-700 hover:text-white"}`}>
                                            {subLink.displayName}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Plan-gated items: render as a non-clickable row with a tooltip
        // explaining the gate. Stays visible (upsell affordance).
        if (link.disabled) {
            return (
                <Tooltip
                    key={index}
                    content={link.disabledReason || FEATURE_GATE_TOOLTIP}
                    placement="right"
                >
                    <div
                        aria-disabled="true"
                        className={`${baseClasses} pointer-events-none opacity-50 cursor-not-allowed`}
                    >
                        {React.cloneElement(link.icon, { className: iconClasses, size: iconSize })}
                        <span className={`${isOpen ? "block opacity-100" : "hidden opacity-0"} ml-3 text-sm transition-all duration-300 ease-in-out text-gray-700`}>
                            {link.displayName}
                        </span>
                    </div>
                </Tooltip>
            );
        }

        return (
            <Link
                key={index}
                to={link.path}
                className={`${baseClasses}`}
            >
                                        {React.cloneElement(link.icon, { className: iconClasses, size: iconSize })}
                <span className={`${isOpen ? "block opacity-100" : "hidden opacity-0"} ml-3 text-sm transition-all duration-300 ease-in-out ${active ? "text-white" : "text-gray-700"}`}>
                    {link.displayName}
                </span>
            </Link>
        );
    };

    return (
        <>
            {/* Desktop Sidebar */}
            <div
                ref={sidebarRef}
                className={`fixed top-16 left-0 h-full z-10 overflow-hidden flex flex-col bg-white shadow-lg transition-all duration-300 ease-in-out ${
                    isOpen ? "w-64" : "w-16"
                } sm:block hidden`}
            >
                {/* Navigation */}
                <nav className="flex flex-col mt-2 mb-40 overflow-y-auto space-y-1 sidebar-scroll px-2 flex-1">
                    {/* Toggle row — sits in the same icon column as the menu items below */}
                    <div
                        onClick={toggleSidebar}
                        className="flex items-center p-3 m-1 rounded-lg cursor-pointer text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                        aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
                    >
                        {isOpen ? <X size={18} /> : <Menu size={18} />}
                        <span
                            className={`${isOpen ? "block opacity-100" : "hidden opacity-0"} ml-3 text-sm transition-all duration-300 ease-in-out`}
                        >
                            Collapse
                        </span>
                    </div>

                    {menuItems.map((link, index) => (
                        isOpen ? (
                            renderMenuItem(link, index)
                        ) : (
                            <Tooltip
                                key={index}
                                placement="right"
                            >
                                <Tooltip.Trigger>
                                    {renderMenuItem(link, index)}
                                </Tooltip.Trigger>
                                <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm">
                                    {link.displayName}
                                    <Tooltip.Arrow />
                                </Tooltip.Content>
                            </Tooltip>
                        )
                    ))}
                </nav>

                {/* Bottom Section — User chip when open, nothing when collapsed.
                    Settings and Sign Out live in the header profile dropdown to
                    avoid duplication. */}
                {isOpen && (
                    <div className="absolute bottom-0 left-0 w-full border-t border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center p-3 rounded-lg">
                            <div className="w-10 h-10 bg-flamingo-400 rounded-full flex items-center justify-center flex-shrink-0">
                                <User size={18} className="text-white" />
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {userInfo?.name || "User"}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                    {userInfo?.persona || "Role"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Hamburger Button */}
            <div className="sm:hidden flex items-center p-4 fixed top-1 left-0 w-full z-50">
                <button
                    onClick={handleMobileSidebarToggle}
                    className="text-gray-700 focus:outline-none hover:text-flamingo-500 transition-colors"
                >
                    {mobileSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Sidebar */}
            <div
                className={`fixed top-0 left-0 h-full z-30 bg-white shadow-lg transform ${
                    mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
                } transition-transform duration-300 w-64 overflow-hidden flex flex-col`}
            >
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <div className="text-lg font-semibold text-gray-800">Menu</div>
                    <button
                        onClick={handleMobileSidebarToggle}
                        className="text-gray-700 focus:outline-none hover:text-flamingo-500 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <nav className="flex flex-col mt-4 space-y-1 p-2 flex-1 overflow-y-auto">
                    {menuItems.map((link, index) => renderMenuItem(link, index, true))}
                </nav>

                {/* Mobile Bottom Section */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {/* User Profile */}
                    <div className="flex items-center mb-4 p-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer">
                        <div className="w-10 h-10 bg-flamingo-400 rounded-full flex items-center justify-center">
                            <User size={18} className="text-white" />
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {userInfo?.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                                {userInfo?.persona || 'Role'}
                            </p>
                        </div>
                    </div>

                    {/* Trial Status */}
                    {/* <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                        <div className="flex items-center mb-2">
                            <Shield size={16} className="text-yellow-600 mr-2" />
                            <span className="text-sm font-medium text-yellow-800">Trial Mode</span>
                        </div>
                        <p className="text-xs text-yellow-700 mb-2">Upgrade to unlock all features</p>
                        <button className="w-full text-xs bg-yellow-500 hover:bg-yellow-600 text-white py-1.5 px-3 rounded-md transition-colors duration-200">
                            Upgrade Now
                        </button>
                    </div> */}

                    {/* Action Buttons */}
                    <div className="space-y-2 mb-8">
                        <button className="w-full flex items-center justify-center p-2.5 text-sm text-gray-600 hover:text-flamingo-600 hover:bg-white rounded-lg transition-all duration-200 border border-gray-200 hover:border-flamingo-300">
                            <Settings size={16} className="mr-2" />
                            Settings
                        </button>
                        <button className="w-full flex items-center justify-center p-2.5 text-sm text-gray-600 hover:text-flamingo-600 hover:bg-white rounded-lg transition-all duration-200 border border-gray-200 hover:border-flamingo-300">
                            <Info size={16} className="mr-2" />
                            Help & Support
                        </button>
                        <button className="w-full flex items-center justify-center p-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 border border-red-200 hover:border-red-300">
                            <LogOut size={16} className="mr-2" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Overlay */}
            {mobileSidebarOpen && (
                <div
                    className="fixed inset-0 z-20 bg-black bg-opacity-50"
                    onClick={handleMobileSidebarToggle}
                />
            )}
        </>
    );
};

export default Sidebar;